import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, getShopSession, clearShopToken } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PinModal } from "@/components/PinModal";
import { ShopHeader } from "@/components/ShopHeader";
import { TodayAttendance } from "@/components/TodayAttendance";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ClipboardEdit, LineChart, Settings2, LifeBuoy, Mail, Gauge } from "lucide-react";

const SUPPORT = "carl@amblemead.co.uk";

export default function ShopLanding() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const [shop, setShop] = useState(null);
  const [err, setErr] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [intent, setIntent] = useState("input");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/shops/by-slug/${slug}`);
        setShop(r.data);
      } catch (e) {
        setErr(e.response?.status === 404 ? "Shop not found." : "Unable to load shop.");
      }
    })();
  }, [slug]);

  const openWith = (which) => {
    setIntent(which);
    // If already have a session with sufficient role, go directly.
    const sess = getShopSession(slug);
    const needsManager = which === "admin" || which === "reports" || which === "kpi";
    if (sess?.token) {
      if (needsManager && !["owner", "manager"].includes(sess.role)) {
        clearShopToken(slug);
      } else {
        nav(`/${slug}/${which}`);
        return;
      }
    }
    setPinOpen(true);
  };

  const onPinSuccess = () => {
    nav(`/${slug}/${intent}`);
  };

  if (err) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-stone-700 shop-bg">
        <div className="font-serif text-2xl">{err}</div>
        <Link to="/login" className="text-emerald-800 mt-4 text-sm underline">Sign in as admin</Link>
      </div>
    );
  }
  if (!shop) {
    return <div className="min-h-screen flex items-center justify-center text-stone-500">{t("common.loading")}</div>;
  }

  const disabled = !shop.is_active;
  const session = getShopSession(slug);

  return (
    <div className="min-h-screen shop-bg">
      <ShopHeader shop={shop} showSignOut />

      <main className="max-w-4xl mx-auto px-6 pt-16 pb-24">
        <div className="text-center mb-12">
          <div className="caption-label mb-3">{shop.town}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-tight tracking-tight">
            {t("client.welcome", { name: shop.name })}
          </h1>
        </div>

        {disabled ? (
          <Card className="border-red-200 bg-red-50/70 max-w-xl mx-auto">
            <CardContent className="p-8 text-center">
              <div className="caption-label text-red-700">Account disabled</div>
              <p className="text-red-900 mt-2 mb-6" data-testid="shop-disabled-message">
                {t("client.disabled_notice")}
              </p>
              <a href={`mailto:${SUPPORT}?subject=Renewal for ${shop.name}`} data-testid="support-btn-disabled">
                <Button className="bg-red-700 hover:bg-red-800">
                  <Mail className="w-4 h-4 mr-1.5" /> {t("client.support")}
                </Button>
              </a>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <button onClick={() => openWith("input")}
                className="group text-left rounded-xl border border-stone-200 bg-white p-6 shadow-sm hover:shadow-md hover:-translate-y-[1px] transition"
                data-testid="input-hub-button">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded bg-emerald-100 text-emerald-800 flex items-center justify-center">
                    <ClipboardEdit className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-serif text-xl">{t("client.input")}</div>
                    <div className="text-sm text-stone-500">Record a service — any staff PIN</div>
                  </div>
                </div>
              </button>
              <button onClick={() => openWith("reports")}
                className="group text-left rounded-xl border border-stone-200 bg-white p-6 shadow-sm hover:shadow-md hover:-translate-y-[1px] transition"
                data-testid="reports-hub-button">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded bg-amber-100 text-amber-800 flex items-center justify-center">
                    <LineChart className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-serif text-xl">{t("client.reports")}</div>
                    <div className="text-sm text-stone-500">Daily worksheet, profit, staff commission</div>
                  </div>
                </div>
              </button>
              <button onClick={() => openWith("admin")}
                className="group text-left rounded-xl border border-stone-200 bg-white p-6 shadow-sm hover:shadow-md hover:-translate-y-[1px] transition"
                data-testid="admin-hub-button">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded bg-stone-900 text-amber-100 flex items-center justify-center">
                    <Settings2 className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-serif text-xl">{t("client.administration")}</div>
                    <div className="text-sm text-stone-500">Staff, menu, income, expenses, licences — owner / manager</div>
                  </div>
                </div>
              </button>
              <button onClick={() => openWith("kpi")}
                className="group text-left rounded-xl border border-stone-200 bg-white p-6 shadow-sm hover:shadow-md hover:-translate-y-[1px] transition"
                data-testid="kpi-hub-button">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded bg-emerald-900 text-amber-100 flex items-center justify-center">
                    <Gauge className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-serif text-xl">{t("client.kpi")}</div>
                    <div className="text-sm text-stone-500">Customers, income and top services — owner / manager</div>
                  </div>
                </div>
              </button>
              <a href={`mailto:${SUPPORT}?subject=Support for ${shop.name}`}
                className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm hover:shadow-md hover:-translate-y-[1px] transition"
                data-testid="support-hub-button">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded bg-stone-100 text-stone-700 flex items-center justify-center">
                    <LifeBuoy className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-serif text-xl">{t("client.support")}</div>
                    <div className="text-sm text-stone-500">Email {SUPPORT}</div>
                  </div>
                </div>
              </a>
            </div>
            <TodayAttendance slug={slug} shop={shop} />
          </>
        )}
      </main>

      <PinModal open={pinOpen} onOpenChange={setPinOpen} slug={slug} intent={intent} onSuccess={onPinSuccess} />
    </div>
  );
}
